import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';

import { LoginPage } from '@/features/auth/LoginPage';
import { meQueryOptions } from '@/features/auth/session';
import { internalHref } from '@/shared/lib/internal-href';

export const Route = createFileRoute('/login')({
  /** Куда вернуть после входа — адрес, с которого сотрудника увели. */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const target = internalHref(search.redirect);
    return target ? { redirect: target } : {};
  },
  // Вошедшему форма входа не нужна: по прямой ссылке на «/login» он уходит в админку,
  // а с непогашенной сменой пароля — на смену пароля.
  beforeLoad: async ({ context }) => {
    const member = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    if (!member) {
      return;
    }
    throw redirect({ to: member.mustChangePassword ? '/change-password' : '/' });
  },
  component: LoginRoute,
});

function LoginRoute() {
  const { redirect: target } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  return (
    <LoginPage
      onAuthenticated={(result) => {
        // Ответ «кто я» относится к прошлой сессии — в том числе к неудачной попытке,
        // закэшированной как 401. Убираем его, чтобы защита разделов спросила заново.
        queryClient.removeQueries({ queryKey: meQueryOptions.queryKey });
        if (result.mustChangePassword) {
          void navigate({ to: '/change-password' });
          return;
        }
        if (target) {
          // Адрес возврата известен только в рантайме, в типах маршрутов его нет — отсюда
          // переход через историю, а не через navigate({ to }). Роутер слушает историю
          // и отработает переход обычным образом, вместе с проверкой сессии в `_app`.
          router.history.replace(target);
          return;
        }
        void navigate({ to: '/' });
      }}
    />
  );
}
